import type {TaskReminderUserOption} from 'src/types/task-and-reminder';
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getAccounts} from 'src/action-creators/accounts';
import {getAllAgents} from 'src/selectors/accounts';
import {TokenListInput} from '@spaced-out/ui-design-system/lib/components/TokenListInput';
import {useFilteredOptions} from '@spaced-out/ui-design-system/lib/hooks/useFilteredOptions';
import {
  BodySmall,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import css from 'src/components/task-and-reminder/form/task-and-reminder-form.css';


type UserSelectProps = {
  label: string;
  placeholder: string;
  selectedKeys: Array<string>;
  onSelectionChange: (selectedKeys: Array<string>) => void;
  className?: string;
  required?: boolean;
  errorText?: string;
  isError?: boolean;
  limit?: number;
};

export const UserSelect = ({
  label,
  placeholder,
  selectedKeys,
  onSelectionChange,
  className,
  required = false,
  errorText,
  isError,
  limit = 50,
}: UserSelectProps) => {
  const dispatch = useDispatch();
  const accountsById = useSelector(getAllAgents);
  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    dispatch(getAccounts());
  }, [dispatch]);

  const users = React.useMemo<Array<TaskReminderUserOption>>(
    () =>
      Object.values(accountsById || {}).map(
        (account: any): TaskReminderUserOption => ({
          key: String(account.id),
          label: account.name || account.email || '',
          secondaryLabel: account.email,
        }),
      ),
    [accountsById],
  );

  const values = React.useMemo(
    () => users.filter((user) => selectedKeys.includes(user.key)),
    [selectedKeys, users],
  );

  const {filteredOptions} = useFilteredOptions({
    searchTerm,
    options: users,
    excludedKeys: selectedKeys,
  });

  return (
    <div className={className}>
      <div className={css.labelContainer}>
        <FormLabelSmall color="secondary">{label}</FormLabelSmall>
        {required && (
          <>
            &nbsp;
            <FormLabelSmall color="danger">{'*'}</FormLabelSmall>
          </>
        )}
      </div>
      <TokenListInput
        values={values}
        placeholder={placeholder}
        onChange={(selectedOptions: Array<TaskReminderUserOption>) => {
          onSelectionChange(selectedOptions.map((option) => option.key));
        }}
        limit={limit}
        menu={{
          options: filteredOptions,
          resolveLabel: (option: TaskReminderUserOption) => option.label || '',
        }}
        onInputChange={(value: string) => {
          setSearchTerm(value);
        }}
        inputValue={searchTerm}
      />
      {isError ? <BodySmall color="danger">{errorText}</BodySmall> : null}
    </div>
  );
};
